import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Eye, Receipt } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PageTransition } from '../components/PageTransition';
import { useMobile } from '../hooks/use-mobile';
import { BottomSheet } from '../components/shared/bottom-sheet';
import { useNotification } from '../contexts/NotificationContext';
import { useApprovalCounts } from '../contexts/ApprovalCountsContext';
import { ExpenseClaim, expenseClaimsService } from '../services/expenseClaimsService';
import { ExpenseClaimsList } from '../components/ExpenseClaimsList';
import { ApprovalScopeToggle } from '../components/approvals/ApprovalScopeToggle';
import { useApprovalScope } from '../hooks/use-approval-scope';

const ExpenseClaimsApprovals: React.FC = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { refreshCounts } = useApprovalCounts();
  const isMobile = useMobile();

  const [loading, setLoading] = useState(true);
  const [pendingClaims, setPendingClaims] = useState<ExpenseClaim[]>([]);
  const [approvedClaims, setApprovedClaims] = useState<ExpenseClaim[]>([]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [approvedListDialogOpen, setApprovedListDialogOpen] = useState(false);
  const [approvedDetailDialogOpen, setApprovedDetailDialogOpen] = useState(false);
  const [approvedDetailTarget, setApprovedDetailTarget] = useState<ExpenseClaim | null>(null);
  const [reviewTarget, setReviewTarget] = useState<ExpenseClaim | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);
  const scope = useApprovalScope();

  const fetchData = useCallback(async () => {
    if (!scope.ready) return;
    try {
      setLoading(true);
      const [pending, approved] = await Promise.all([
        expenseClaimsService.getPending(scope.onlyMine),
        expenseClaimsService.getApproved(scope.onlyMine),
      ]);
      setPendingClaims(pending);
      setApprovedClaims(approved);
    } catch (error) {
      console.error('Failed to fetch expense claim approvals:', error);
      showNotification(
        t('expenseClaims.messages.fetchError', 'Failed to load expense claims'),
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, [scope.ready, scope.onlyMine, showNotification, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openReviewDialog = (claim: ExpenseClaim) => {
    setReviewTarget(claim);
    setReviewNote(claim.reviewNote || '');
    setReviewDialogOpen(true);
  };

  const submitReview = async (status: 'APPROVED' | 'REJECTED') => {
    if (!reviewTarget) return;

    try {
      setReviewSaving(true);
      await expenseClaimsService.review(
        reviewTarget.id,
        status,
        reviewNote.trim() || undefined,
      );
      showNotification(
        status === 'APPROVED'
          ? t('expenseClaims.messages.approved', 'Expense claim approved')
          : t('expenseClaims.messages.rejected', 'Expense claim rejected'),
        'success',
      );
      setReviewDialogOpen(false);
      setReviewTarget(null);
      setReviewNote('');
      await fetchData();
      await refreshCounts();
    } catch (error) {
      console.error('Failed to review expense claim:', error);
      showNotification(
        t('expenseClaims.messages.reviewError', 'Failed to review expense claim'),
        'error',
      );
    } finally {
      setReviewSaving(false);
    }
  };

  return (
    <PageTransition className="p-4 sm:p-6 space-y-6">
      <div className="space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {t('expenseClaims.approvals.title', 'Approvazioni note spese')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t(
                  'expenseClaims.approvals.subtitle',
                  'Revisiona le richieste inviate e consulta lo storico approvato.',
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <ApprovalScopeToggle scope={scope} />
            <Button
              type="button"
              variant="outline"
              onClick={() => setApprovedListDialogOpen(true)}
              className="w-full gap-2 sm:w-auto"
            >
              <Eye className="h-4 w-4" />
              {t('expenseClaims.actions.viewApproved', 'Vedi approvate')}
            </Button>
          </div>
        </div>
      </div>

      <ExpenseClaimsList
        claims={pendingClaims}
        loading={loading}
        withoutContainer
        showReviewSummaryAction
        onOpenReviewSummary={openReviewDialog}
      />

      {isMobile ? (
        <BottomSheet
          open={reviewDialogOpen}
          onClose={() => setReviewDialogOpen(false)}
          title={t('expenseClaims.review.summaryTitle', 'Recap nota spese #{{id}}', {
            id: reviewTarget?.id,
          })}
          description={
            reviewTarget
              ? `${reviewTarget.date.slice(0, 10)} - EUR ${Number(reviewTarget.amount).toFixed(2)}`
              : ''
          }
          contentClassName="space-y-4"
        >
          {reviewTarget && (
            <div className="space-y-3">
              <div className="rounded-md border p-3 text-sm space-y-1">
                <p><strong>{t('expenseClaims.list.userLabel', 'Utente')}:</strong> {reviewTarget.user?.name || '-'}</p>
                <p><strong>{t('expenseClaims.form.category', 'Categoria')}:</strong> {reviewTarget.category}</p>
                <p><strong>{t('expenseClaims.form.notes', 'Note')}:</strong> {reviewTarget.notes || '-'}</p>
                <p>
                  <strong>{t('expenseClaims.form.receiptAttached', 'Scontrino allegato')}:</strong>{' '}
                  {reviewTarget.hasReceipt ? t('common.yes', 'Si') : t('common.no', 'No')}
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">{t('expenseClaims.form.attachments', 'Allegati')}</p>
                {(reviewTarget.attachments?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('expenseClaims.approvedHistory.noAttachments', 'Nessun allegato disponibile.')}</p>
                ) : (
                  reviewTarget.attachments.map((attachment) => {
                    const isImage = attachment.mimeType?.startsWith('image/');

                    return (
                      <div
                        key={attachment.id || attachment.fileUrl}
                        className="rounded-md border p-2 space-y-2"
                      >
                        {isImage && (
                          <a href={attachment.fileUrl} target="_blank" rel="noreferrer" className="block">
                            <img
                              src={attachment.fileUrl}
                              alt={t('expenseClaims.form.attachments', 'Allegati')}
                              className="max-h-64 w-full rounded-md object-contain bg-muted/20"
                            />
                          </a>
                        )}
                        <Button type="button" variant="outline" className="w-full justify-center" asChild>
                          <a href={attachment.fileUrl} target="_blank" rel="noreferrer" className="inline-flex w-full items-center justify-center gap-1">
                            <Download className="h-4 w-4" />
                            {t('common.download', 'Scarica')}
                          </a>
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="review-note-mobile">
              {t('expenseClaims.review.noteOptional', 'Review note (optional)')}
            </Label>
            <Textarea
              id="review-note-mobile"
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              placeholder={t('expenseClaims.review.commonPlaceholder', 'Aggiungi una nota per approvazione o rifiuto')}
            />
          </div>

          <div className="sticky bottom-0 -mx-4 grid grid-cols-3 gap-2 border-t bg-background px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setReviewDialogOpen(false)}
              disabled={reviewSaving}
            >
              {t('common.close', 'Chiudi')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="w-full"
              onClick={() => submitReview('REJECTED')}
              disabled={reviewSaving}
            >
              {reviewSaving ? t('expenseClaims.review.saving', 'Saving...') : t('expenseClaims.actions.reject', 'Reject')}
            </Button>
            <Button
              type="button"
              className="w-full"
              onClick={() => submitReview('APPROVED')}
              disabled={reviewSaving}
            >
              {reviewSaving ? t('expenseClaims.review.saving', 'Saving...') : t('expenseClaims.actions.approve', 'Approve')}
            </Button>
          </div>
        </BottomSheet>
      ) : (
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto [&>button]:hidden">
            <DialogHeader>
              <DialogTitle>
                {t('expenseClaims.review.summaryTitle', 'Recap nota spese #{{id}}', {
                  id: reviewTarget?.id,
                })}
              </DialogTitle>
              <DialogDescription>
                {reviewTarget
                  ? `${reviewTarget.date.slice(0, 10)} - EUR ${Number(reviewTarget.amount).toFixed(2)}`
                  : ''}
              </DialogDescription>
            </DialogHeader>

            {reviewTarget && (
              <div className="space-y-3">
                <div className="rounded-md border p-3 text-sm space-y-1">
                  <p><strong>{t('expenseClaims.list.userLabel', 'Utente')}:</strong> {reviewTarget.user?.name || '-'}</p>
                  <p><strong>{t('expenseClaims.form.category', 'Categoria')}:</strong> {reviewTarget.category}</p>
                  <p><strong>{t('expenseClaims.form.notes', 'Note')}:</strong> {reviewTarget.notes || '-'}</p>
                  <p>
                    <strong>{t('expenseClaims.form.receiptAttached', 'Scontrino allegato')}:</strong>{' '}
                    {reviewTarget.hasReceipt ? t('common.yes', 'Si') : t('common.no', 'No')}
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">{t('expenseClaims.form.attachments', 'Allegati')}</p>
                  {(reviewTarget.attachments?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('expenseClaims.approvedHistory.noAttachments', 'Nessun allegato disponibile.')}</p>
                  ) : (
                    reviewTarget.attachments.map((attachment) => {
                      const isImage = attachment.mimeType?.startsWith('image/');

                      return (
                        <div
                          key={attachment.id || attachment.fileUrl}
                          className="rounded-md border p-2 space-y-2"
                        >
                          {isImage && (
                            <a href={attachment.fileUrl} target="_blank" rel="noreferrer" className="block">
                              <img
                                src={attachment.fileUrl}
                                alt={t('expenseClaims.form.attachments', 'Allegati')}
                                className="max-h-64 w-full rounded-md object-contain bg-muted/20"
                              />
                            </a>
                          )}
                          <Button type="button" variant="outline" className="w-full justify-center" asChild>
                            <a href={attachment.fileUrl} target="_blank" rel="noreferrer" className="inline-flex w-full items-center justify-center gap-1">
                              <Download className="h-4 w-4" />
                              {t('common.download', 'Scarica')}
                            </a>
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="review-note">
                {t('expenseClaims.review.noteOptional', 'Review note (optional)')}
              </Label>
              <Textarea
                id="review-note"
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder={t('expenseClaims.review.commonPlaceholder', 'Aggiungi una nota per approvazione o rifiuto')}
              />
            </div>

            <DialogFooter className="w-full gap-2">
              <Button
                type="button"
                variant="outline"
                className="mr-auto"
                onClick={() => setReviewDialogOpen(false)}
                disabled={reviewSaving}
              >
                {t('common.close', 'Chiudi')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => submitReview('REJECTED')}
                disabled={reviewSaving}
              >
                {reviewSaving ? t('expenseClaims.review.saving', 'Saving...') : t('expenseClaims.actions.reject', 'Reject')}
              </Button>
              <Button
                type="button"
                onClick={() => submitReview('APPROVED')}
                disabled={reviewSaving}
              >
                {reviewSaving ? t('expenseClaims.review.saving', 'Saving...') : t('expenseClaims.actions.approve', 'Approve')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={approvedListDialogOpen} onOpenChange={setApprovedListDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('expenseClaims.approvedHistory.title', 'Note spese approvate')}</DialogTitle>
            <DialogDescription>
              {t(
                'expenseClaims.approvedHistory.subtitle',
                'Seleziona una nota spese approvata per vedere il dettaglio e scaricare gli allegati.',
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {approvedClaims.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('expenseClaims.approvedHistory.empty', 'Nessuna nota spese approvata trovata.')}
              </p>
            ) : (
              approvedClaims.map((claim) => (
                <button
                  key={claim.id}
                  type="button"
                  onClick={() => {
                    setApprovedDetailTarget(claim);
                    setApprovedDetailDialogOpen(true);
                  }}
                  className="w-full rounded-md border px-3 py-2 text-left hover:bg-muted/40"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">
                        #{claim.id} - {claim.user?.name || t('expenseClaims.list.userLabel', 'Utente')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {claim.date.slice(0, 10)} - {claim.category} - EUR {Number(claim.amount).toFixed(2)}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {claim.attachments?.length ?? 0} {t('expenseClaims.form.attachments', 'Allegati')}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={approvedDetailDialogOpen} onOpenChange={setApprovedDetailDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {t('expenseClaims.approvedHistory.detailTitle', 'Dettaglio nota spese #{{id}}', {
                id: approvedDetailTarget?.id,
              })}
            </DialogTitle>
            <DialogDescription>
              {approvedDetailTarget
                ? `${approvedDetailTarget.date.slice(0, 10)} - EUR ${Number(approvedDetailTarget.amount).toFixed(2)}`
                : ''}
            </DialogDescription>
          </DialogHeader>

          {approvedDetailTarget && (
            <div className="space-y-3">
              <div className="rounded-md border p-3 text-sm space-y-1">
                <p><strong>{t('expenseClaims.list.userLabel', 'Utente')}:</strong> {approvedDetailTarget.user?.name || '-'}</p>
                <p><strong>{t('expenseClaims.form.category', 'Categoria')}:</strong> {approvedDetailTarget.category}</p>
                <p><strong>{t('expenseClaims.form.notes', 'Note')}:</strong> {approvedDetailTarget.notes || '-'}</p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">{t('expenseClaims.form.attachments', 'Allegati')}</p>
                {(approvedDetailTarget.attachments?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('expenseClaims.approvedHistory.noAttachments', 'Nessun allegato disponibile.')}</p>
                ) : (
                  approvedDetailTarget.attachments.map((attachment) => {
                    const isImage = attachment.mimeType?.startsWith('image/');

                    return (
                      <div key={attachment.id || attachment.fileUrl} className="rounded-md border p-2 space-y-2">
                        {isImage && (
                          <a href={attachment.fileUrl} target="_blank" rel="noreferrer" className="block">
                            <img
                              src={attachment.fileUrl}
                              alt={t('expenseClaims.form.attachments', 'Allegati')}
                              className="max-h-64 w-full rounded-md object-contain bg-muted/20"
                            />
                          </a>
                        )}
                        <Button type="button" variant="outline" size="sm" asChild>
                          <a href={attachment.fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1">
                            <Download className="h-4 w-4" />
                            {t('common.download', 'Scarica')}
                          </a>
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
};

export default ExpenseClaimsApprovals;
